// React
import React, { useEffect, useState } from "react";

// Components
import Table from "components/Common/Table/Table";
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import CheckCell from "../CheckCell/CheckCell";
import Button from "components/Common/Button/Button";

// Types
import {
    Company,
    COMPANY_LIST_ENUM, FUND_ENUM,
    SelectableCompany,
    UserCompany,
} from "./Company.types";

// Data
import { enabledOptions, teamOptions } from "assets/data/lists";

// Context
import { useSettings } from "context/Settings.context";

//Utils
import {
    disableCompanies,
    enableCompanies,
    enableCompany,
    getUserCompanies,
    parseBool,
    resetCompaniesToDefault,
} from "utils/notifications.utlis";
import { getAllCompanies } from "utils/companies.utils";

// Styles
import CompanySettingsContainer from "./CompanySettings.styles";

const CompanySettings: React.FC = () => {
    const { setShowLoadingToast } = useSettings();

    const [loading, setLoading] = useState<boolean>(true);
    const [companyFilterQuery, setCompanyFilterQuery] = useState<any>();
    const [allCompanies, setAllCompanies] = useState<Company[]>([]);
    const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
    const [selectableCompanies, setSelectableCompanies] = useState<
        SelectableCompany[] | null
    >();
    const [filteredCompanies, setFilteredCompanies] =
        useState<SelectableCompany[]>();

    const companiesColumns = [
        {
            id: 1,
            title: "COMPANY",
            sortable: false,
            value: "company",
            cell: (company: string) => <p>{company}</p>,
            show: true,
        },
        {
            id: 2,
            title: "Enabled",
            sortable: false,
            value: "data",
            cell: (data: {
                companyRefId: number;
                enabled: boolean;
                userCompanyMapId: string;
            }) => (
                <CheckCell
                    configId={""}
                    updateCompanySelection={updateCompanySelection}
                    companyRefId={data.companyRefId}
                    enabled={data.enabled}
                    userCompanyMapId={data.userCompanyMapId}
                />
            ),
            show: true,
        },
    ];

    const companyListOptions = [
        {
            label: "Focus list",
            value: COMPANY_LIST_ENUM.FOCUS_LIST,
        },
        {
            label: "Portfolio",
            value: COMPANY_LIST_ENUM.PORTFOLIO,
        },
    ];

    const fundOptions = [
        {
            label: "Global",
            value: FUND_ENUM.GLOBAL,
        },
        {
            label: "Asia",
            value: FUND_ENUM.ASIA,
        },
    ];

    const filterCompaniesOptions = [
        { id: 1, name: "Search COMPANY", value: "company", type: "SEARCH" },
        {
            id: 2,
            name: "TEAM",
            value: "gim_subteam",
            type: "DROPDOWN",
            options: teamOptions,
        },
        {
            id: 3,
            name: "STATUS",
            value: "enabled",
            type: "DROPDOWN",
            options: enabledOptions,
        },
        {
            id: 4,
            name: "COMPANY LIST",
            value: "companyList",
            type: "DROPDOWN",
            options: companyListOptions,
        },
        {
            id: 5,
            name: "FUND",
            value: "fund",
            type: "DROPDOWN",
            options: fundOptions,
        },
    ];

    const getSetCompanies = async (loader: boolean) => {
        setLoading(loader);
        const result = await getAllCompanies();
        const data = await result.json();
        setAllCompanies(data.data);

        const userCompaniesData = await getUserCompanies();
        setUserCompanies(userCompaniesData);
        if (loader) setLoading(false);
    };

    const resetDefaultCompanies = async () => {
        setLoading(true);
        setShowLoadingToast({
            show: true,
            message: "UPDATING CONFIGURATION",
        });

        await resetCompaniesToDefault();
        setShowLoadingToast({
            show: false,
            message: "",
        });

        getSetCompanies(true);
    };

    const enableAllFilteredCompanies = async () => {
        if (!filteredCompanies || filteredCompanies.length === 0) return;

        // No user companies means all of them are already enabled
        if (!userCompanies.length) return;

        setLoading(true);
        setShowLoadingToast({
            show: true,
            message: "ENABLING ALL FILTERED COMPANIES",
        });

        const companiesToEnable = filteredCompanies
            .filter((c) => !c.data.enabled)
            .filter((c) => c.data.companyRefId)
            .map((c) => ({
                company_ref_id: c.data.companyRefId,
            }));

        try {
            await enableCompanies(companiesToEnable);
            await getSetCompanies(true);
            setShowLoadingToast({
                show: false,
                message: "",
            });
        } catch (error) {
            setShowLoadingToast({
                show: true,
                message: "ERROR ENABLING COMPANIES",
            });
            setTimeout(() => {
                setShowLoadingToast({
                    show: false,
                    message: "",
                });
            }, 2000);
            setLoading(false);
        }
    };

    const disableAllFilteredCompanies = async () => {
        if (!filteredCompanies || filteredCompanies.length === 0) return;

        // With current data model it's impossible to disable all the company notifications
        if (filteredCompanies.length === selectableCompanies?.length) {
            return;
        }

        setLoading(true);
        setShowLoadingToast({
            show: true,
            message: "DISABLING ALL FILTERED COMPANIES",
        });

        try {
            if (!userCompanies.length) {
                // When all companies are enabled, no records exists, so we need to create
                // records for those that are enabled. The rest, which are filtered, will be automatically disabled
                const filteredCompanyIds = filteredCompanies
                    .map((c) => c.data.companyRefId)
                    .filter((id) => !!id);
                const companiesToEnable = selectableCompanies
                    ?.filter((c) => c.data.companyRefId)
                    ?.filter(
                        (c) => !filteredCompanyIds.includes(c.data.companyRefId)
                    )
                    ?.map((c) => ({
                        company_ref_id: c.data.companyRefId,
                    }));

                await enableCompanies(companiesToEnable);
                await getSetCompanies(true);
                setShowLoadingToast({
                    show: false,
                    message: "",
                });
            } else {
                // When some companies are disabled, records already exist
                // so to disable it's only required to delete some of them
                const companiesToDisable = filteredCompanies
                    .filter((c) => c.data.enabled)
                    .filter((c) => !!c.data.userCompanyMapId)
                    .map((c) => c.data.userCompanyMapId);
                await disableCompanies(companiesToDisable);
                await getSetCompanies(true);
                setShowLoadingToast({
                    show: false,
                    message: "",
                });
            }
        } catch (error) {
            setShowLoadingToast({
                show: true,
                message: "ERROR DISABLING COMPANIES",
            });
            setTimeout(() => {
                setShowLoadingToast({
                    show: false,
                    message: "",
                });
            }, 2000);
            setLoading(false);
        }
    };

    const filterUserSelectedCompanies = () => {
        const companySettings = allCompanies
            .map((company: Company) => {
                return {
                    id: company.company_ref_id,
                    company: company.company,
                    gim_subteam: company.gim_subteam,
                    data: {
                        companyRefId: company.company_ref_id,
                        enabled: checkForEnabledCompany(company.company_ref_id),
                        userCompanyMapId: getUserCompanyMapId(
                            company.company_ref_id
                        ),
                        live_company_flag: company.live_company_flag === 1,
                        live_company_asia_flag: company.live_company_asia_flag === 1,
                        isInFocusList: company.live_company_flag === 1 || company.live_company_asia_flag === 1,
                        isInPortfolio: company.portfolio_flag === 1,
                    },
                };
            })
            .filter((company) => company.company !== "");
        setSelectableCompanies(companySettings);
    };

    const handleDefaultDeviation = async (companyRefId?: number) => {
        const companies = allCompanies
            .filter((company) => company.company_ref_id !== companyRefId)
            .map((company) => {
                return {
                    company_ref_id: company.company_ref_id,
                };
            });
        await enableCompanies(companies);
    };

    const updateCompanySelection = async (
        enabled: boolean,
        companyRefId?: number,
        userCompanyMapId?: string
    ) => {
        setShowLoadingToast({
            show: true,
            message: "UPDATING CONFIGURATION",
        });

        userCompanies.length
            ? enabled
                ? await enableCompany(companyRefId)
                : await disableCompanies([userCompanyMapId])
            : await handleDefaultDeviation(companyRefId);
        await getSetCompanies(false);
        setShowLoadingToast({
            show: false,
            message: "",
        });
    };

    const checkForEnabledCompany = (companyRefId: number) => {
        if (!userCompanies.length) return true;
        if (!companyRefId) return false;
        const companyRefs = userCompanies.map(
            (company: UserCompany) => company.company_ref_id
        );
        return companyRefs.includes(companyRefId);
    };

    const getUserCompanyMapId = (companyRefId: number) => {
        let id: string = "";
        userCompanies.map((company: UserCompany) => {
            if (company?.company_ref_id === companyRefId) id = company.id;
        });
        return id;
    };

    const filterByCompany = (companies: SelectableCompany[]) => {
        if (!companyFilterQuery.company) return companies;
        return companies.filter((company: SelectableCompany) =>
            company.company
                .toLowerCase()
                .includes(companyFilterQuery.company.toLowerCase())
        );
    };

    const filterByTeam = (companies: SelectableCompany[]) => {
        if (!companyFilterQuery.gim_subteam) return companies;
        return companies.filter((company: SelectableCompany) =>
            company.gim_subteam
                .toLowerCase()
                .includes(companyFilterQuery.gim_subteam.toLowerCase())
        );
    };

    const filterByEnabled = (companies: SelectableCompany[]) => {
        if (!companyFilterQuery.enabled) return companies;
        return companies.filter(
            (company: SelectableCompany) =>
                company.data.enabled === parseBool(companyFilterQuery.enabled)
        );
    };

    const filterByCompanyList = (companies: SelectableCompany[]) => {
        if (!companyFilterQuery.companyList) return companies;
        return companies.filter(c => {
            if (companyFilterQuery.companyList === COMPANY_LIST_ENUM.FOCUS_LIST) {
                return c.data.isInFocusList
            } else if (companyFilterQuery.companyList === COMPANY_LIST_ENUM.PORTFOLIO) {
                return c.data.isInPortfolio
            } else {
                return false;
            }
        })
    }

    const filterByFund = (companies: SelectableCompany[]) => {
        if (!companyFilterQuery.fund) return companies;
        return companies.filter(c => {
            if (companyFilterQuery.fund === FUND_ENUM.GLOBAL) {
                return c.data.live_company_flag
            } else if (companyFilterQuery.fund === FUND_ENUM.ASIA) {
                return c.data.live_company_asia_flag
            } else {
                return false;
            }
        })
    }

    useEffect(() => {
        if (selectableCompanies && companyFilterQuery) {
            const filteredByCompany = filterByCompany(selectableCompanies);
            const filteredByTeam = filterByTeam(filteredByCompany);
            const filteredByEnabled = filterByEnabled(filteredByTeam);
            const filteredByCompanyList = filterByCompanyList(filteredByEnabled);
            const filteredByFund = filterByFund(filteredByCompanyList)
            setFilteredCompanies(filteredByFund);
        } else if (selectableCompanies) {
            setFilteredCompanies(selectableCompanies);
        }
    }, [companyFilterQuery, selectableCompanies]);

    useEffect(() => {
        filterUserSelectedCompanies();
    }, [userCompanies]);

    useEffect(() => {
        getSetCompanies(true);
    }, []);

    return (
        <CompanySettingsContainer>
            <p className="title">COMPANIES</p>
            <div className="button-container">
                <Button text="RESET TO DEFAULT" click={resetDefaultCompanies} />
                <Button
                    text="ENABLE ALL"
                    click={enableAllFilteredCompanies}
                    disabled={filteredCompanies?.length === 0}
                />
                <Button
                    text="DISABLE ALL"
                    click={disableAllFilteredCompanies}
                    disabled={
                        filteredCompanies?.length ===
                            selectableCompanies?.length ||
                        filteredCompanies?.length === 0
                    }
                />
            </div>
            <div className="searchBarCompanies">
                <SearchOptions
                    setFilterQuery={setCompanyFilterQuery}
                    options={filterCompaniesOptions}
                    localFilter
                />
            </div>
            <Table
                data={filteredCompanies}
                columns={companiesColumns}
                filterQuery={companyFilterQuery}
                loading={loading}
                pagination={false}
            />
        </CompanySettingsContainer>
    );
};

export default CompanySettings;
